import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wifi, Hash, Lock, Eye, EyeOff, ArrowRight, Info } from "lucide-react";
import { usePortalAuth } from "./PortalAuthContext";
import { useBranding } from "../hooks/useBranding";
import { errMsg } from "./portalApi";

export const PortalLoginPage = () => {
  const [noLayanan, setNoLayanan] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = usePortalAuth();
  const branding = useBranding();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(noLayanan, password);
      navigate("/portal");
    } catch (err) {
      setError(errMsg(err, "Nomor layanan atau kata sandi salah"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060a14] flex flex-col justify-center items-center p-4 relative overflow-hidden">
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-emerald-600/15 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-600/15 rounded-full blur-[128px] pointer-events-none" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-500 via-cyan-500 to-blue-600 shadow-xl shadow-emerald-500/20 mb-3 border border-emerald-400/30 overflow-hidden">
            {branding.app_logo ? <img src={branding.app_logo} alt="Logo" className="w-full h-full object-contain" /> : <Wifi className="w-7 h-7 text-white" />}
          </div>
          <h1 className="text-2xl font-bold text-white tracking-wide" data-testid="portal-login-title">{branding.app_name || "NUSA NET FIBER"}</h1>
          <p className="text-sm text-slate-400 mt-1">Portal Pelanggan — cek tagihan, bayar & layanan</p>
        </div>

        <form onSubmit={submit} className="bg-[#0d1527] border border-slate-800 rounded-2xl p-6 shadow-2xl space-y-4" data-testid="portal-login-form">
          <div>
            <h2 className="text-base font-bold text-white">Masuk Portal</h2>
            <p className="text-xs text-slate-400">Gunakan nomor layanan yang tertera di invoice Anda</p>
          </div>
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs" data-testid="portal-login-error">{error}</div>}
          <div>
            <label className="block text-xs text-slate-300 mb-1">Nomor Layanan</label>
            <div className="relative">
              <Hash className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={noLayanan} onChange={(e) => setNoLayanan(e.target.value)} placeholder="CUST-2026-001" required
                className="w-full pl-9 pr-3 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white font-mono uppercase focus:border-emerald-500 outline-none" data-testid="portal-login-no-layanan" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-300 mb-1">Kata Sandi</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••" required
                className="w-full pl-9 pr-10 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white focus:border-emerald-500 outline-none" data-testid="portal-login-password" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-[11px] text-cyan-200">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Kata sandi awal: <b className="font-mono">123456</b>. Ubah setelah masuk melalui menu Akun.</span>
          </div>
          <button type="submit" disabled={loading} className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60 transition-opacity" data-testid="portal-login-submit">
            {loading ? "Memeriksa..." : "Masuk Portal"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
        <p className="text-center text-xs text-slate-500 mt-5">
          Petugas/operator? <Link to="/login" className="text-cyan-400 hover:underline" data-testid="portal-to-operator-login">Login Operator</Link>
        </p>
        <div className="text-center mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-slate-500" data-testid="portal-legal-footer-links">
          <Link to="/tentang-kami" className="hover:text-cyan-300" data-testid="portal-footer-link-tentang">Tentang Kami</Link>
          <span>•</span>
          <Link to="/kebijakan-privasi" className="hover:text-cyan-300" data-testid="portal-footer-link-privasi">Kebijakan Privasi</Link>
          <span>•</span>
          <Link to="/syarat-ketentuan" className="hover:text-cyan-300" data-testid="portal-footer-link-syarat">Syarat &amp; Ketentuan</Link>
          <span>•</span>
          <Link to="/kontak" className="hover:text-cyan-300" data-testid="portal-footer-link-kontak">Kontak</Link>
        </div>
      </div>
    </div>
  );
};
